import {
  login,
  authenticateSuccess,
  cancelLogin,
  authenticateFail,
  logout,
  setUserInfo,
  registerSuccess,
  registerError,
  cancelRegister,
  registerRequest,
  logOutSuccess,
  authenticationTwoFa,
  verifyTwoFa,
  verifyTwoFaFail,
  registerSubAccountSuccess,
  registerSubAccountError,
  registerSubAccountRequest,
  forgotPasswordSuccess,
  forgotPasswordError,
  forgotPasswordRequest,
  resetPasswordSuccess,
  resetPasswordError,
  resetPasswordRequest,
  oauthGoogle,
} from '../reducers/auth';
import {
  take,
  call,
  put,
  fork,
  cancel,
  takeLatest,
  takeEvery,
  all,
} from 'redux-saga/effects';
import API from 'api';
import { getUserInfo } from 'store/action';

function* loginSaga({ email, password }) {
  const res = yield call(API.login, email, password);
  if (res.status === 200) {
    if (res.data?.accessToken) {
      setLocalStorage(res.data);
      yield put(authenticateSuccess());
      yield put(getUserInfo());
    } else {
      yield put(authenticationTwoFa());
      const { verify2FaToken } = res.data;

      while (true) {
        const { payload } = yield take(verifyTwoFa);
        const { verifyCode } = payload;

        const resVerify = yield call(API.verifyTwoFa, {
          verifyCode,
          verifyToken: verify2FaToken,
        });
        if (resVerify.status === 200) {
          setLocalStorage(resVerify.data);
          yield put(authenticateSuccess());
          yield put(getUserInfo());
          return;
        } else {
          yield put(verifyTwoFaFail(resVerify.message));
        }
      }
    }
  } else {
    yield put(authenticateFail(res.message));
  }
}

function setLocalStorage({ accessToken, refreshToken, expirationTime }) {
  window.localStorage.setItem('accessToken', accessToken);
  window.localStorage.setItem('refreshToken', refreshToken);
  window.localStorage.setItem('expirationTime', expirationTime);
}

function* fetchUserInfo() {
  const [res, resKey] = yield all([call(API.getUserInfo), call(API.getApiKey)]);
  if (res.status === 200 && resKey.status === 200) {
    yield put(setUserInfo({ ...res.data, apiKey: resKey.data.apiKey }));
  }
}

function* logoutUser() {
  yield call(API.logOut);
  yield put(logOutSuccess());
}

function* registerUser(payload) {
  const res = yield call(API.register, payload);
  if (res.status === 200) {
    yield put(registerSuccess());
  } else {
    yield put(registerError(res.message));
  }
}

function* forgotPasswordSaga(action) {
  const res = yield call(API.fortgotPassword, action.payload);
  if (res.status === 200) {
    yield put(forgotPasswordSuccess());
  } else {
    yield put(forgotPasswordError(res.message));
  }
}

function* registerUserSaga() {
  while (true) {
    const { payload } = yield take(registerRequest);
    const task = yield fork(registerUser, payload);

    const { type } = yield take([
      cancelRegister,
      registerError,
      registerSuccess,
    ]);

    if (type === 'auth/cancelRegister') {
      yield cancel(task);
    }
  }
}

function* authentication() {
  while (true) {
    const action = yield take(login);
    const { email, password } = action.payload || {};
    const task = yield fork(loginSaga, { email, password });
    const ac = yield take([
      cancelLogin,
      authenticateFail,
      authenticateSuccess,
      logout,
    ]);

    if (ac?.type === 'auth/cancelLogin' || ac.type === 'auth/logout') {
      yield cancel(task);
    }
  }
}

function* registerSubAccount(action) {
  const body = action.payload;
  const res = yield call(API.registerSubAccount, body);

  if (res?.status === 200) {
    yield put(registerSubAccountSuccess());
    return;
  }

  yield put(registerSubAccountError(res?.message));
}

function* resetPassword(action) {
  const body = action.payload;
  const res = yield call(API.resetPassword, body);

  if (res?.status === 200) {
    yield put(resetPasswordSuccess());
    return;
  }

  yield put(resetPasswordError(res?.message));
}

function* authGoogleSaga(action) {
  const idToken = action.payload;
  const res = yield call(API.oauthGoogleAPI, idToken);
  if (res.status === 200) {
    setLocalStorage(res.data);
    yield put(authenticateSuccess());
    yield put(getUserInfo());
    return;
  }
}

export default function* rootAuth() {
  yield all([
    authentication(),
    takeLatest(getUserInfo, fetchUserInfo),
    takeEvery(logout, logoutUser),
    registerUserSaga(),
    takeLatest(registerSubAccountRequest, registerSubAccount),
    takeLatest(forgotPasswordRequest, forgotPasswordSaga),
    takeLatest(resetPasswordRequest, resetPassword),
    takeEvery(oauthGoogle, authGoogleSaga),
  ]);
}
